'use client';

// Browser side of Tranzila Hosted Fields wallets. Loaded ONLY when the server says the
// wallet flag is on — nothing here runs (and no Tranzila script is fetched) otherwise.
//
// Docs (2026-10-12):
//   • Apple Pay: <script src="https://hf.tranzila.com/assets/js/thfapplepay.js">, show the
//     button when `window.ApplePaySession` exists, then `hfChargeApple(params, cb(err, res))`.
//   • Google Pay: <script src="https://hf.tranzila.com/assets/js/thostedf.js">,
//     `fields = TzlaHostedFields.create({ sandbox, fields: { credit_card_number: … } })`,
//     `fields.onEvent('googleIsEnable', …)` → show the button, then
//     `fields.chargeGpay(params, cb(err, res))`. HF needs a credit_card_number field to
//     initialise; we mount it in a hidden container and never show or use it — this app
//     never collects a card number. Unverified (Tranzila has no Apple Pay sandbox).
// We never patch their scripts and never call their internal endpoints. We DO observe the
// ApplePaySession their script creates (read-only listeners, see observeApplePaySessions):
// their script has no cancel handler, and Apple's own `cancel` / `paymentauthorized` events
// are the only way to know a dismissed sheet charged nothing.

export type WalletMethod = 'apple_pay' | 'google_pay';
/** 'mock' = no Tranzila (simulated sheet). 'live' = real Hosted Fields (allowlisted sessions only). */
export type WalletMode = 'mock' | 'live';
export type AllowedMethods = { apple_pay: boolean; google_pay: boolean };

const HF_SCRIPT = 'https://hf.tranzila.com/assets/js/thostedf.js';
const APPLE_SCRIPT = 'https://hf.tranzila.com/assets/js/thfapplepay.js';

declare global {
  interface Window {
    ApplePaySession?: any;
    TzlaHostedFields?: any;
    hfChargeApple?: (params: Record<string, unknown>, cb: (err: any, res: any) => void) => void;
  }
}

const loaded = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  if (!loaded.has(src)) {
    loaded.set(
      src,
      new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => {
          loaded.delete(src);
          reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(s);
      }),
    );
  }
  return loaded.get(src)!;
}

function appleAvailable(): boolean {
  try {
    return !!window.ApplePaySession && !!window.ApplePaySession.canMakePayments?.();
  } catch {
    return false;
  }
}

let hfFields: any = null;

/** iPhone / Android phone detection — only used by mock (dev) mode; live mode is capability-based. */
export function phonePlatform(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): 'iphone' | 'android' | null {
  if (/iPhone/.test(ua)) return 'iphone'; // iPadOS reports as Mac → excluded (not a phone)
  if (/Android/.test(ua) && /Mobile/.test(ua)) return 'android'; // Android tablets omit "Mobile"
  return null;
}

/**
 * Which ONE wallet this browser should show — decided by capability, not by device name
 * (same rule on every device, as Stripe / Shopify do):
 *   1. Apple Pay when the browser supports it and it's set up (Safari on iPhone / iPad / Mac,
 *      and Chrome & co. on iOS 16+);
 *   2. otherwise Google Pay when Tranzila's frame reports it ready (Android, desktop Chrome /
 *      Edge / Firefox — and an iPhone without Apple Pay);
 *   3. otherwise nothing (the regular Checkout stays).
 * Never both: two competing wallet buttons convert worse than the right one.
 */
export async function detectWallet(mode: WalletMode, allowed: AllowedMethods): Promise<WalletMethod | null> {
  if (typeof window === 'undefined') return null;
  const platform = phonePlatform();
  if (mode === 'mock') {
    // Local/dev only: allow desktop so the flow can be exercised without a phone.
    const devDesktop = !platform && process.env.NODE_ENV !== 'production';
    if (platform === 'iphone' || (devDesktop && /Macintosh/.test(navigator.userAgent) && 'ApplePaySession' in window)) {
      return allowed.apple_pay ? 'apple_pay' : null;
    }
    if (platform === 'android' || devDesktop) return allowed.google_pay ? 'google_pay' : null;
    return null;
  }
  const loadApple = async (): Promise<WalletMethod | null> => {
    observeApplePaySessions(); // before their script loads, in case it keeps a reference
    await loadScript(APPLE_SCRIPT);
    return typeof window.hfChargeApple === 'function' ? 'apple_pay' : null;
  };
  if (allowed.apple_pay && appleAvailable()) {
    const m = await loadApple().catch(() => null);
    if (m) return m;
  }
  if (!allowed.google_pay) return null; // Google Pay sub-flag off → no Tranzila script at all
  try {
    await loadScript(HF_SCRIPT);
  } catch {
    return null;
  }
  if (!window.TzlaHostedFields) return null;
  return new Promise<WalletMethod | null>((resolve) => {
    let host = document.getElementById('tzla-hf-hidden');
    if (!host) {
      host = document.createElement('div');
      host.id = 'tzla-hf-hidden';
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
      host.innerHTML = '<div id="tzla-hf-card"></div>';
      document.body.appendChild(host);
    }
    const timer = setTimeout(() => resolve(null), 5000);
    try {
      hfFields = window.TzlaHostedFields.create({
        sandbox: false, // Tranzila has no sandbox for wallets
        fields: { credit_card_number: { selector: '#tzla-hf-card', placeholder: '', tabindex: -1 } },
      });
      hfFields.onEvent('googleIsEnable', () => {
        clearTimeout(timer);
        resolve('google_pay');
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

export interface HostedFieldsParams {
  terminal_name: string;
  thtk: string;
  amount: string;
  currency_code: 'USD';
  tran_mode: 'V'; // Apple Pay supports only A / V; we only ever send V
  tokenize: boolean;
  response_language: 'english';
  labelApple: string;
  attempt_ref: string;
}

export type WalletOutcome =
  | { outcome: 'success'; transactionId: string }
  | { outcome: 'cancelled' }
  | { outcome: 'error'; transactionId?: string; clientError?: string }
  /** Tranzila's script never called back (its Apple Pay code has no cancel handler). */
  | { outcome: 'no_response'; clientError: string };

/**
 * Card networks we accept in the wallet sheet. American Express and Diners are not
 * supported by our processing (Diners cards ride the Discover network in Apple Pay), so
 * Apple greys those cards out in the sheet instead of letting the payment fail later.
 */
export const ACCEPTED_NETWORKS = ['visa', 'masterCard'];

/** How long to wait for Tranzila's callback before asking the server to check with Tranzila. */
export const WALLET_CALLBACK_TIMEOUT_MS = 90_000;
/** After the sheet closes (page regains focus), how long to wait for a late result. */
export const SHEET_CLOSED_GRACE_MS = 2_500;
/** Google Pay runs in Tranzila's cross-origin frame (no cancel signal at all) — react faster. */
export const GPAY_CLOSED_GRACE_MS = 1_000;

/** Short, card-data-free description of whatever the script handed us. */
function describe(err: any): string | undefined {
  if (!err) return undefined;
  if (typeof err === 'string') return err.slice(0, 200);
  const parts = [err.name, err.error, err.message, err.code].filter((x) => typeof x === 'string' || typeof x === 'number');
  return (parts.length ? parts.join(': ') : JSON.stringify(err)).slice(0, 200);
}

function parse(err: any, res: any): WalletOutcome {
  const tr = res?.transaction_response;
  const txn = tr?.transaction_id ? String(tr.transaction_id) : undefined;
  if (tr?.success === true && txn) return { outcome: 'success', transactionId: txn };
  // The wallet sheet being dismissed surfaces as an error without a transaction.
  const msg = JSON.stringify(err ?? tr?.error ?? '').toLowerCase();
  if (!txn && /cancel|abort|closed|dismiss/.test(msg)) return { outcome: 'cancelled' };
  return { outcome: 'error', transactionId: txn, clientError: describe(err ?? tr?.error) };
}

/**
 * Read-only observer for Tranzila's Apple Pay requests. thfapplepay.js silently drops a
 * non-200 / non-JSON / error reply from /processapple/* (no callback, no completePayment),
 * so the sheet says "Payment not completed" and the page would wait forever. We only READ
 * the response of those two URLs (status + Tranzila's error code/message — never card
 * data) and hand a failure to the active callback. Requests are not modified.
 */
let activeTzlaFailure: ((summary: string) => void) | null = null;
let xhrObserverInstalled = false;
export function summarizeTzla(status: number, text: string): { ok: boolean; summary: string } {
  let j: any = null;
  try {
    j = JSON.parse(text);
  } catch {
    /* not JSON */
  }
  if (status !== 200) return { ok: false, summary: `http ${status}` };
  if (!j || typeof j !== 'object') return { ok: false, summary: `non-JSON reply (${text.slice(0, 60).replace(/\s+/g, ' ')})` };
  const tr = j.transaction_response;
  if (j.error || (j.error_code != null && j.error_code !== 0 && j.message !== 'Success')) {
    return { ok: false, summary: `error ${j.error_code ?? ''} ${typeof j.error === 'string' ? j.error : ''} ${j.message ?? ''}`.trim() };
  }
  if (tr && tr.success !== true && !tr.transaction_id) {
    return { ok: false, summary: `declined code ${tr.processor_response_code ?? '?'} ${tr.error ?? ''}`.trim() };
  }
  return { ok: true, summary: 'ok' };
}
function installTzlaObserver() {
  if (xhrObserverInstalled || typeof XMLHttpRequest === 'undefined') return;
  xhrObserverInstalled = true;
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
    const u = String(url);
    if (u.includes('hf.tranzila.com/processapple/')) {
      const step = u.includes('validate') ? 'validate' : 'process';
      this.addEventListener('loadend', () => {
        let text = '';
        try {
          text = this.responseText;
        } catch {
          /* ignore */
        }
        const r = summarizeTzla(this.status, text);
        if (!r.ok) activeTzlaFailure?.(`tranzila ${step}: ${r.summary}`.slice(0, 200));
      });
    }
    return (origOpen as any).call(this, method, url, ...rest);
  } as typeof XMLHttpRequest.prototype.open;
}

/**
 * Read-only observer for the ApplePaySession Tranzila's script creates. Their script never
 * handles Apple's `cancel` event, so a dismissed sheet used to look exactly like a payment
 * whose callback got lost — and the page had to ask Tranzila for minutes before letting the
 * customer try again. Wrapping the constructor lets us ADD listeners to the session (their
 * handlers, behaviour and the payment itself are untouched): `paymentauthorized` = the
 * customer approved with Face ID / Touch ID; `cancel` before that = nothing can have been
 * charged.
 */
let activeAppleSession: ((session: EventTarget) => void) | null = null;
let appleObserverInstalled = false;
export function observeApplePaySessions() {
  if (appleObserverInstalled || typeof window === 'undefined' || !window.ApplePaySession) return;
  const Original = window.ApplePaySession;
  try {
    window.ApplePaySession = new Proxy(Original, {
      construct(target, args) {
        const session = Reflect.construct(target, args) as EventTarget;
        try {
          activeAppleSession?.(session);
        } catch {
          /* observing must never break the payment */
        }
        return session;
      },
      // Static helpers (canMakePayments, supportsVersion, STATUS_*) keep their native `this`.
      get(target, prop) {
        const v = Reflect.get(target, prop, target);
        return typeof v === 'function' ? v.bind(target) : v;
      },
    });
    appleObserverInstalled = true;
  } catch {
    window.ApplePaySession = Original;
  }
}

/**
 * Opens the wallet sheet. MUST be called synchronously from the tap handler (no await
 * before it), or Safari / Chrome will refuse to show the sheet.
 */
export function chargeWallet(
  method: WalletMethod,
  mode: WalletMode,
  p: HostedFieldsParams,
  /** A callback that arrives AFTER the watchdog fired (e.g. a slow sheet) is passed here. */
  onLate?: (o: WalletOutcome) => void,
): Promise<WalletOutcome> {
  if (mode === 'mock') {
    return new Promise((r) => setTimeout(() => r({ outcome: 'success', transactionId: `mock-${Date.now()}` }), 900));
  }
  return new Promise((resolve) => {
    let settled = false;
    const watchdog = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ outcome: 'no_response', clientError: `no callback from Tranzila ${method} script within ${WALLET_CALLBACK_TIMEOUT_MS / 1000}s` });
    }, WALLET_CALLBACK_TIMEOUT_MS);
    // Tranzila's Apple Pay script has no cancel handler, so a cancelled / closed sheet calls
    // back nothing. When the page regains focus (the sheet closed) and no result follows
    // shortly, report "sheet closed" — the server then confirms with Tranzila's lookup
    // that nothing was reserved (or picks the hold up if there is one).
    let closeTimer: ReturnType<typeof setTimeout> | null = null;
    const onReturn = () => {
      if (settled || document.visibilityState === 'hidden' || closeTimer) return;
      closeTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve({
          outcome: 'no_response',
          clientError: `wallet sheet closed without a result${seenTzlaMsgs.length ? ` (tranzila msgs: ${seenTzlaMsgs.join(',')})` : ''}`,
        });
      }, method === 'google_pay' ? GPAY_CLOSED_GRACE_MS : SHEET_CLOSED_GRACE_MS);
    };
    const armAt = Date.now();
    const onFocus = () => {
      if (Date.now() - armAt > 800) onReturn(); // ignore the focus blip as the sheet opens
    };
    // Google Pay: Tranzila's frame sends NO message when the sheet is dismissed (only a
    // chargeResponse when a charge finishes), and on Android the native sheet doesn't reliably
    // fire focus/visibility on our page. A touch on OUR page after the sheet opened means the
    // sheet is gone (it covers the page while open) → same "sheet closed" path, which asks
    // Tranzila right away instead of waiting for the 90s watchdog + cron.
    const onPageTouch = () => {
      if (method === 'google_pay') onFocus();
    };
    // Diagnostics: any other message Tranzila's frame posts during the charge (type only).
    const seenTzlaMsgs: string[] = [];
    const onTzlaMsg = (e: MessageEvent) => {
      if (!/tranzila\.com$/.test(new URL(e.origin || 'http://x').hostname)) return;
      let type = 'unparsed';
      try {
        type = String(JSON.parse(e.data)?.type ?? 'untyped');
      } catch {
        /* not JSON */
      }
      if (type !== 'chargeResponse' && seenTzlaMsgs.length < 5) seenTzlaMsgs.push(type.slice(0, 30));
    };
    // Google Pay: focus/visibility events don't always fire when the sheet closes (seen: two
    // dismissals only caught by the 2-minute cron). Poll focus instead — once the page has lost
    // focus to the sheet and has it back, the sheet is gone.
    let sawBlur = false;
    const focusPoll =
      method === 'google_pay'
        ? setInterval(() => {
            if (typeof document.hasFocus !== 'function') return;
            if (!document.hasFocus()) sawBlur = true;
            else if (sawBlur) onFocus();
          }, 500)
        : null;
    const cleanup = () => {
      if (focusPoll) clearInterval(focusPoll);
      activeTzlaFailure = null;
      activeAppleSession = null;
      clearTimeout(watchdog);
      if (closeTimer) clearTimeout(closeTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      document.removeEventListener('pointerdown', onPageTouch, true);
      window.removeEventListener('message', onTzlaMsg);
    };
    const cb = (err: any, res: any) => {
      const o = parse(err, res);
      if (settled) return onLate?.(o);
      settled = true;
      cleanup();
      resolve(o);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    document.addEventListener('pointerdown', onPageTouch, true);
    window.addEventListener('message', onTzlaMsg);
    // thfapplepay.js calls an undefined `fCallbak` (typo) on its error path; route that to
    // our callback instead of letting it throw silently. We don't modify their script.
    (window as any).fCallbak = cb;
    if (method === 'apple_pay') {
      installTzlaObserver();
      activeTzlaFailure = (summary) => cb({ error: 'tranzila', message: summary }, null);
      observeApplePaySessions();
      // Apple's own signals. Cancel BEFORE the customer authorized → nothing can have been
      // charged → report 'cancelled' at once (no Tranzila check, no wait). Cancel AFTER
      // authorizing (e.g. Apple's timeout mid-charge) → money may have moved, so the normal
      // no-response path below still asks Tranzila.
      let authorized = false;
      activeAppleSession = (session) => {
        session.addEventListener('paymentauthorized', () => {
          authorized = true;
        });
        session.addEventListener('cancel', () => {
          if (authorized || settled) return;
          settled = true;
          cleanup();
          resolve({ outcome: 'cancelled' });
        });
      };
    }
    const common = {
      terminal_name: p.terminal_name,
      amount: p.amount,
      currency_code: p.currency_code,
      tran_mode: p.tran_mode,
      thtk: p.thtk,
      ...(p.tokenize ? { tokenize: true } : {}),
      response_language: p.response_language,
      // Extra fields are echoed back in user_form_data — correlation only.
      attempt_ref: p.attempt_ref,
    };
    try {
      if (method === 'apple_pay') {
        window.hfChargeApple!({ ...common, labelApple: p.labelApple, supported_network: ACCEPTED_NETWORKS }, cb);
      } else {
        hfFields.chargeGpay(common, cb);
      }
    } catch (e) {
      cb(e, null);
    }
  });
}
